import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";

const conversation = [
  { role: "user", text: "Why am I craving sweets at night?" },
  {
    role: "ai",
    header: "Cross-signal analysis",
    text:
      "Three converging patterns this week — your sleep midpoint drifted 47 minutes later, dinner protein averaged 18g (target 30–35g), and your post-lunch glucose spiked > 160 mg/dL on 4 of 5 days.",
    tags: ["Sleep · low HRV", "Under-eaten dinner", "Glucose volatility"],
  },
  {
    role: "ai",
    header: "What your body is asking for",
    text:
      "Fast-glucose fuel to compensate for a metabolic dip you engineered earlier in the day. It&rsquo;s not lack of discipline — it&rsquo;s biology on a predictable loop.",
    tags: ["Cortisol-driven craving"],
  },
  {
    role: "ai",
    header: "Try tonight",
    text:
      "Add 25g whey + a handful of walnuts to your evening. Screens off by 22:30. If the craving still hits, 4 squares of 85% dark chocolate is fine — it satisfies without the crash.",
    tags: ["Protein +", "Sleep window", "Managed indulgence"],
  },
];

export default function AICoach() {
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (step >= conversation.length) return;
    const t = setTimeout(() => setStep((s) => s + 1), step === 0 ? 700 : 1400);
    return () => clearTimeout(t);
  }, [step]);

  return (
    <section id="coach" data-testid="coach-section" className="relative py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <div className="absolute right-0 top-1/3 h-[420px] w-[420px] bg-radial-teal opacity-70" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-[1fr,1.15fr] gap-10 lg:gap-16 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-teal-soft">06 — AI Coach</div>
            <h2 className="mt-4 font-heading text-4xl sm:text-5xl tracking-tighter leading-[1.05] font-medium">
              Ask anything.
              <br /><span className="gradient-text-teal-lav">Get an answer that knows you.</span>
            </h2>
            <p className="mt-5 text-zinc-400 max-w-lg">
              Not a generic chatbot. HealthIRL&rsquo;s coach reasons across your food, biomarkers, sleep and behavior — and gives you the one thing to change today.
            </p>
            <ul className="mt-6 space-y-2.5 text-sm text-zinc-300">
              {[
                "Uses your data, never a generic template",
                "Explains *why* — always with evidence",
                "One high-leverage action at a time",
              ].map((p) => (
                <li key={p} className="flex items-center gap-2.5">
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-teal-mid/20 text-teal-soft">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 5 5L20 7"/></svg>
                  </span>
                  {p}
                </li>
              ))}
            </ul>
          </div>

          <div className="relative">
            <div className="glass-strong rounded-3xl p-5 sm:p-6 shadow-[0_30px_120px_-30px_rgba(20,184,166,0.4)]">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-teal-soft animate-pulse-glow" />
                  <span className="text-[10px] uppercase tracking-[0.22em] text-teal-soft">Coach · live</span>
                </div>
                <span className="text-[10px] font-mono text-zinc-500">private · on-device context</span>
              </div>

              <div className="space-y-3 min-h-[340px]">
                {conversation.slice(0, step).map((m, i) =>
                  m.role === "user" ? (
                    <motion.div key={i} initial={{ y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.4 }}
                      className="flex justify-end" data-testid={`coach-msg-user-${i}`}>
                      <div className="max-w-[80%] rounded-2xl rounded-br-sm bg-teal-mid/15 border border-teal-mid/25 px-4 py-2.5 text-sm text-white">
                        {m.text}
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div key={i} initial={{ y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.5 }}
                      className="max-w-[92%] rounded-2xl rounded-bl-sm bg-white/[0.03] border border-white/[0.08] px-4 py-3" data-testid={`coach-msg-ai-${i}`}>
                      <div className="text-[10px] uppercase tracking-[0.22em] text-lav-400">{m.header}</div>
                      <p className="mt-1 text-sm text-zinc-100 leading-relaxed">{m.text}</p>
                      {m.tags && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {m.tags.map((t) => (
                            <span key={t} className="text-[10px] uppercase tracking-wider text-teal-soft border border-teal-mid/25 bg-teal-mid/[0.06] rounded-full px-2 py-0.5">{t}</span>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  )
                )}

                {step < conversation.length && (
                  <div className="flex items-center gap-1.5 pt-1 pl-2 text-lav-400">
                    <span className="h-1.5 w-1.5 rounded-full bg-lav-500 animate-pulse" />
                    <span className="h-1.5 w-1.5 rounded-full bg-lav-500 animate-pulse [animation-delay:0.15s]" />
                    <span className="h-1.5 w-1.5 rounded-full bg-lav-500 animate-pulse [animation-delay:0.3s]" />
                  </div>
                )}
              </div>

              {step >= conversation.length && (
                <button data-testid="coach-replay-btn" onClick={() => setStep(0)}
                  className="mt-4 text-[11px] uppercase tracking-wider text-teal-soft hover:text-teal-mid">
                  ↻ Replay conversation
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
